// components/ActivitySummary.js — Premium redesign
// Rich per-type renderers · Plus Jakarta Sans · achievement glow ·
// campaign verified badge · no duplicate chips · clean private/anon states

import React from "react";
import { Box, Typography, Link, Chip, Avatar } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  VisibilityOff as PrivateIcon,
  EmojiEvents   as TrophyIcon,
  Verified      as VerifiedIcon,
} from "@mui/icons-material";
import {
  getCleanDisplayName,
  isCreatorActivity,
  getCreatorIdentifier,
  getActivityPhrase,
  getCampaignCompany,
  getActivityAccentColor,
} from "./activityHelpers";

/* ─────────────────────────────────────────────────────────
   Shared
───────────────────────────────────────────────────────── */
const FONT = "'Plus Jakarta Sans', sans-serif";

/** Stable link sx used throughout */
const LINK_SX = {
  fontWeight: 800,
  textDecoration: "none",
  color: "text.primary",
  fontFamily: FONT,
  "&:hover": { textDecoration: "underline" },
};

/* ─────────────────────────────────────────────────────────
   BodyText — shared base for plain post / generic text
───────────────────────────────────────────────────────── */
function BodyText({ children, isPrivate, sx }) {
  return (
    <Typography
      sx={{
        fontSize:     "0.96rem",
        lineHeight:   1.65,
        wordBreak:    "break-word",
        whiteSpace:   "pre-wrap",
        overflowWrap: "break-word",
        maxWidth:     "100%",
        fontFamily:   FONT,
        color:        isPrivate ? "text.secondary" : "text.primary",
        fontStyle:    isPrivate ? "italic" : "normal",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/* ─────────────────────────────────────────────────────────
   AchievementCard — gold gradient box with glow orb
───────────────────────────────────────────────────────── */
function AchievementCard({ achievement, linkTo }) {
  const theme = useTheme();
  if (!achievement) return null;

  const titleNode = (
    <Typography
      sx={{
        fontWeight: 800, fontSize: "0.92rem", fontFamily: FONT,
        lineHeight: 1.3, mb: 0.4,
        color: "text.primary",
      }}
    >
      {achievement.blitzbolt_title}
    </Typography>
  );

  return (
    <Box
      sx={{
        mt: 1, p: "12px 14px",
        borderRadius: 3,
        background: `linear-gradient(135deg,
          ${alpha("#f59e0b", 0.09)} 0%,
          ${alpha("#d97706", 0.05)} 100%)`,
        border: `1px solid ${alpha("#f59e0b", 0.22)}`,
        position: "relative", overflow: "hidden",
      }}
    >
      {/* ambient glow */}
      <Box sx={{
        position: "absolute", top: -20, right: -20,
        width: 80, height: 80, borderRadius: "50%",
        background: alpha("#f59e0b", 0.18),
        filter: "blur(22px)", pointerEvents: "none",
      }} />

      <Box display="flex" gap={1.5} alignItems="flex-start">
        {/* trophy badge */}
        <Box sx={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 2.5,
          background: "linear-gradient(135deg, #d97706 0%, #fbbf24 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 3px 12px ${alpha("#d97706", 0.4)}`,
        }}>
          <TrophyIcon sx={{ color: "white", fontSize: 20 }} />
        </Box>

        <Box flex={1} minWidth={0}>
          {linkTo ? (
            <Link
              component={RouterLink} to={linkTo}
              sx={{ ...LINK_SX, fontSize: "0.92rem", display: "block" }}
            >
              {achievement.blitzbolt_title}
            </Link>
          ) : titleNode}

          {achievement.blitzbolt_description && (
            <Typography
              sx={{
                color: "text.secondary", fontSize: "0.82rem",
                lineHeight: 1.55, fontFamily: FONT,
                wordBreak: "break-word",
              }}
            >
              {achievement.blitzbolt_description}
            </Typography>
          )}

          {achievement.points_earned != null && (
            <Chip
              label={`+${achievement.points_earned} pts`}
              size="small"
              sx={{
                mt: 1, height: 20, fontSize: "0.68rem", fontWeight: 800,
                letterSpacing: "0.02em",
                bgcolor: alpha("#f59e0b", 0.14), color: "#d97706",
                border: `1px solid ${alpha("#f59e0b", 0.28)}`,
                "& .MuiChip-label": { px: 0.85 },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   CastingCallChips — stats row for full (showHeader=true) view
───────────────────────────────────────────────────────── */
function CastingCallChips({ castingCall }) {
  const theme = useTheme();
  if (!castingCall) return null;

  const chips = [
    castingCall.budget > 0 && {
      label: `$${Number(castingCall.budget).toLocaleString()} budget`,
      color: "#10b981",
    },
    castingCall.applicant_count != null && {
      label: `${castingCall.applicant_count} applicant${castingCall.applicant_count === 1 ? "" : "s"}`,
      color: "#0891b2",
    },
    castingCall.call_type && {
      label: castingCall.call_type === "creator" ? "Creator Call" : "General Work",
      color: "#8b5cf6",
    },
  ].filter(Boolean);

  if (!chips.length) return null;

  return (
    <Box display="flex" flexWrap="wrap" gap={0.6} mt={0.85}>
      {chips.map(({ label, color }) => (
        <Chip
          key={label} label={label} size="small"
          sx={{
            height: 22, fontSize: "0.7rem", fontWeight: 700,
            fontFamily: FONT,
            color,
            bgcolor: alpha(color, 0.09),
            border: `1px solid ${alpha(color, 0.2)}`,
            "& .MuiChip-label": { px: 0.85 },
          }}
        />
      ))}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   DisplayNameBlock — name with profile link, private state,
   anonymous state, and campaign company override.
   Used in showHeader=true mode.
───────────────────────────────────────────────────────── */
function DisplayNameBlock({ activity }) {
  const { is_private, user_id } = activity;
  const displayName       = getCleanDisplayName(activity);
  const isCreatorAct      = isCreatorActivity(activity);
  const creatorIdentifier = isCreatorAct ? getCreatorIdentifier(activity) : null;

  // ── Campaign company override ─────────────────────────
  // If the campaign has a resolved company, show the company name as the
  // top-level actor instead of the individual who created it.
  const campaignCompany = activity.activity_type === "Campaign"
    ? getCampaignCompany(activity)
    : null;

  if (campaignCompany) {
    return (
      <Box display="flex" alignItems="center" gap={0.75}>
        {campaignCompany.photo && (
          <Avatar
            src={campaignCompany.photo}
            alt={campaignCompany.name}
            sx={{ width: 18, height: 18, border: "1px solid", borderColor: "divider" }}
          />
        )}
        <Typography
          sx={{ fontWeight: 800, fontFamily: FONT, fontSize: "0.97rem", color: "text.primary" }}
        >
          {campaignCompany.name}
        </Typography>
      </Box>
    );
  }

  if (is_private || displayName === "Anonymous User") {
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <PrivateIcon sx={{ fontSize: 15, opacity: 0.5, color: "text.secondary" }} />
        <Typography
          sx={{ fontWeight: 600, fontFamily: FONT, color: "text.secondary", fontSize: "0.95rem" }}
        >
          {displayName}
        </Typography>
      </Box>
    );
  }

  if (isCreatorAct && creatorIdentifier) {
    return (
      <Link
        component={RouterLink} to={`/profile/${creatorIdentifier}`}
        sx={{ ...LINK_SX, fontSize: "0.97rem" }}
      >
        {displayName}
      </Link>
    );
  }

  if (user_id) {
    return (
      <Link
        component={RouterLink} to={`/profile/${user_id}`}
        sx={{ ...LINK_SX, fontSize: "0.97rem" }}
      >
        {displayName}
      </Link>
    );
  }

  return (
    <Typography sx={{ fontWeight: 800, fontFamily: FONT, fontSize: "0.97rem", color: "text.primary" }}>
      {displayName}
    </Typography>
  );
}

/* ─────────────────────────────────────────────────────────
   Content renderers per activity type
   These are the BODY only — header (name/time) is separate.
───────────────────────────────────────────────────────── */

/** Post body — plain text */
function PostContent({ activity }) {
  const phrase = getActivityPhrase(activity);
  if (!phrase) return null;
  return (
    <BodyText isPrivate={activity.is_private} sx={{ mt: 0.25 }}>
      {phrase}
    </BodyText>
  );
}

/** Achievement — golden card */
function AchievementContent({ activity, showHeader }) {
  const { achievement, is_private } = activity;
  if (!achievement) return null;

  const isCreatorAct      = isCreatorActivity(activity);
  const creatorIdentifier = isCreatorAct ? getCreatorIdentifier(activity) : null;
  const linkTo            = !is_private && isCreatorAct && creatorIdentifier
    ? `/profile/${creatorIdentifier}` : null;

  return <AchievementCard achievement={achievement} linkTo={linkTo} />;
}

/** CastingCall — text + chips (chips only in full/showHeader view) */
function CastingCallContent({ activity, showHeader }) {
  const { casting_call, is_private } = activity;
  const phrase = getActivityPhrase(activity);
  return (
    <Box>
      <BodyText isPrivate={is_private} sx={{ mt: showHeader ? 0.5 : 0.15 }}>
        {phrase}
      </BodyText>
      {/* Only render chips in standalone (showHeader=true) mode.
          When embedded in FeedItem (showHeader=false), FeedItem's own
          CastingCallStats component renders the chips to avoid duplication. */}
      {showHeader && <CastingCallChips castingCall={casting_call} />}
    </Box>
  );
}

/** Campaign — launch phrase only.
 *  Company attribution is now handled in the header (DisplayNameBlock /
 *  FeedItem display name), so the CampaignBadge pill is intentionally
 *  removed from the body to avoid duplication.
 */
function CampaignContent({ activity }) {
  const { activity_name, is_private } = activity;
  return (
    <Typography
      sx={{
        fontSize: "0.96rem", lineHeight: 1.65, fontFamily: FONT,
        wordBreak: "break-word",
        color: is_private ? "text.secondary" : "text.primary",
      }}
    >
      launched{" "}
      <Box component="span" sx={{ fontWeight: 700 }}>{activity_name}</Box>
    </Typography>
  );
}

/** Generic — payout, partnership, join, payment, pitch, reply, user */
function GenericContent({ activity }) {
  const phrase = getActivityPhrase(activity);
  return (
    <BodyText isPrivate={activity.is_private} sx={{ mt: 0.15 }}>
      {phrase}
    </BodyText>
  );
}

/* ─────────────────────────────────────────────────────────
   Content dispatcher
───────────────────────────────────────────────────────── */
function ActivityContent({ activity, showHeader }) {
  const type = activity.activity_type?.trim();
  switch (type) {
    case "Post":        return <PostContent        activity={activity} />;
    case "Achievement": return <AchievementContent  activity={activity} showHeader={showHeader} />;
    case "CastingCall": return <CastingCallContent  activity={activity} showHeader={showHeader} />;
    case "Campaign":    return <CampaignContent     activity={activity} />;
    default:            return <GenericContent      activity={activity} />;
  }
}

/* ─────────────────────────────────────────────────────────
   DATE CHIP — shown at bottom in standalone mode
───────────────────────────────────────────────────────── */
function DateLine({ dateStr }) {
  if (!dateStr) return null;
  const label = new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric",
  });
  return (
    <Typography
      sx={{
        mt: 1, fontSize: "0.8rem", color: "text.disabled",
        fontFamily: FONT, fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN EXPORT
───────────────────────────────────────────────────────── */
export default function ActivitySummary({
  activity,
  showHeader = true,
  showDate   = true,
}) {
  const { activity_date } = activity;

  return (
    <Box>
      {/* Name / author */}
      {showHeader && (
        <Box mb={0.35}>
          <DisplayNameBlock activity={activity} />
        </Box>
      )}

      {/* Body content (type-specific) */}
      <ActivityContent activity={activity} showHeader={showHeader} />

      {/* Date */}
      {showDate && <DateLine dateStr={activity_date} />}
    </Box>
  );
}